import { useLocation } from "wouter";
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package,
  Folder,
  Phone,
  Archive,
  LogOut,
  Home,
  ChevronRight,
  Settings,
  Bell,
  User,
  Search,
  Menu,
  Tag,
  X,
  PlusCircle,
  LayoutDashboard,
  ChevronDown,
  Calendar,
  Users,
  Moon,
  Sun,
  Plus,
  FileText
} from "lucide-react";
import { ProductsTab } from "@/components/admin/ProductsTab";
import { CategoriesTab } from "@/components/admin/CategoriesTab";
import { ScheduleCallsTab } from "@/components/admin/ScheduleCallsTab";
import { UsedMachineryTab } from "@/components/admin/UsedMachineryTab";
import { UsedCategoriesTab } from "@/components/admin/UsedCategoriesTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { SparePartsTab } from "@/components/admin/SparePartsTab";
import { CatalogsTab } from "@/components/admin/CatalogsTab";
import logoBlanco from "../assets/logo-blanco.png";
import { motion, AnimatePresence } from "framer-motion";
import Particles from "react-particles";
import type { Engine } from "tsparticles-engine";
import { loadSlim } from "tsparticles-slim";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";

// Estilos CSS personalizados para la scrollbar
const customScrollbarStyles = `
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
`;

export default function AdminPanel() {
  const [, setLocation] = useLocation();
  const [view, setView] = useState<"launcher" | "detail">("launcher");
  const [activeTab, setActiveTab] = useState<string>("products");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const [searchResults, setSearchResults] = useState<{
    products: { id: string | number; name: string; type: "product" }[];
    categories: { id: string | number; name: string; type: "category" }[];
    usedMachinery: { id: string | number; name: string; type: "used" }[];
    usedCategories: { id: string | number; name: string; type: "used-category" }[];
    spareParts: { id: string | number; name: string; type: "spare-part" }[];
  }>({
    products: [],
    categories: [],
    usedMachinery: [],
    usedCategories: [],
    spareParts: []
  });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true); // Nuevo estado para el tema
  const { toast } = useToast();
  const [fabOpen, setFabOpen] = useState(false);

  const userData = JSON.parse(localStorage.getItem("adminUser") || '{"username": "Administrador"}');
  const username = typeof userData === 'string' ? userData : userData.username || "Administrador";

  // Añadir datos para el dashboard
  const [dashboardStats, setDashboardStats] = useState({
    productsTotal: 247,
    productsNew: 12,
    categoriesTotal: 32,
    categoriesNew: 2,
    usedMachineryTotal: 18,
    usedMachineryNew: 5,
    callsTotal: 8,
    callsPending: 3,
    recentActivity: [
      { id: 1, action: "Producto añadido", item: "Embutidora E-300", user: "Carlos", time: "Hace 2 horas" },
      { id: 2, action: "Categoría actualizada", item: "Cortadoras", user: "Maria", time: "Hace 5 horas" },
      { id: 3, action: "Llamada programada", item: "Cliente: Industrias Cárnicas S.L.", user: "Sistema", time: "Hace 1 día" },
      { id: 4, action: "Maquinaria de ocasión", item: "Mezcladora M-200", user: "Juan", time: "Hace 2 días" },
    ],
    topProducts: [
      { name: "Embutidora E-200", views: 156, percentage: 85 },
      { name: "Cortadora C-3000", views: 129, percentage: 70 },
      { name: "Amasadora A-500", views: 98, percentage: 55 },
    ]
  });

  // Configuración para partículas de fondo
  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
  }, []);

  // Efecto para mostrar la pantalla de bienvenida durante unos segundos
  useEffect(() => {
    if (showWelcomeScreen) {
      const timer = setTimeout(() => {
        setShowWelcomeScreen(false);
        toast({
          title: "¡Bienvenido al Panel de Administración!",
          description: "Gestiona tus productos, categorías y más desde aquí.",
          action: (
            <ToastAction altText="Entendido">Entendido</ToastAction>
          ),
        });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showWelcomeScreen, toast]);

  useEffect(() => {
    // Comprobar si hay token de admin o token de Supabase
    const adminToken = localStorage.getItem("adminToken");
    const supabaseToken = localStorage.getItem("supabase.auth.token");
    const userStr = localStorage.getItem("supabase.auth.user");

    let isAuthorized = false;

    // Verificar token de admin (método antiguo)
    if (adminToken) {
      isAuthorized = true;
    }

    // Verificar si es un usuario de Supabase con rol admin
    if (supabaseToken && userStr) {
      try {
        const userData = JSON.parse(userStr);
        if (userData?.user_metadata?.role === 'admin' ||
            userData?.user_metadata?.is_admin === true) {
          isAuthorized = true;

          // Asegurarse de que tenga los datos de admin para mantener compatibilidad
          if (!adminToken) {
            localStorage.setItem("adminToken", "admin-token");
            localStorage.setItem("adminUser", JSON.stringify({
              username: userData.email || 'Administrador',
              role: 'admin'
            }));
          }
        }
      } catch (e) {
        console.error("Error al parsear datos de usuario:", e);
      }
    }

    if (!isAuthorized) {
      setLocation("/admin/login");
    }
  }, [setLocation]);

  // Efecto para buscar cuando cambie la consulta de búsqueda
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim().length < 2) {
        setShowSearchResults(false);
        return;
      }

      // Simulación de búsqueda (en una implementación real, esto consultaría la API)
      // En un caso real, aquí harías llamadas a las distintas API endpoints con el término de búsqueda

      const searchLower = searchQuery.toLowerCase();

      // Buscar en productos (ejemplo simulado)
      const mockProducts = [
        { id: 1, name: "Embutidora E-200", type: "product" as const },
        { id: 2, name: "Cortadora C-3000", type: "product" as const },
        { id: 3, name: "Amasadora A-500", type: "product" as const }
      ].filter(item => item.name.toLowerCase().includes(searchLower));

      // Buscar en categorías
      const mockCategories = [
        { id: 1, name: "Embutidoras", type: "category" as const },
        { id: 2, name: "Cortadoras", type: "category" as const },
        { id: 3, name: "Procesadoras", type: "category" as const }
      ].filter(item => item.name.toLowerCase().includes(searchLower));

      // Buscar en maquinaria usada
      const mockUsedMachinery = [
        { id: 1, name: "Embutidora Vemag 2010", type: "used" as const },
        { id: 2, name: "Cortadora Treif 2018", type: "used" as const },
        { id: 3, name: "Mezcladora Laska 2015", type: "used" as const }
      ].filter(item => item.name.toLowerCase().includes(searchLower));

      // Buscar en categorías de ocasión
      const mockUsedCategories = [
        { id: 1, name: "Embutidoras de segunda mano", type: "used-category" as const },
        { id: 2, name: "Cortadoras de ocasión", type: "used-category" as const },
        { id: 3, name: "Procesadoras usadas", type: "used-category" as const }
      ].filter(item => item.name.toLowerCase().includes(searchLower));

      // Buscar en recambios
      const mockSpareParts = [
        { id: 1, name: "Motor para embutidora", type: "spare-part" as const },
        { id: 2, name: "Cuchilla para cortadora", type: "spare-part" as const },
        { id: 3, name: "Bomba hidráulica", type: "spare-part" as const }
      ].filter(item => item.name.toLowerCase().includes(searchLower));

      setSearchResults({
        products: mockProducts,
        categories: mockCategories,
        usedMachinery: mockUsedMachinery,
        usedCategories: mockUsedCategories,
        spareParts: mockSpareParts
      });

      setShowSearchResults(true);
    };

    const timer = setTimeout(performSearch, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Cerrar el menú de usuario cuando se hace clic fuera
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-menu') && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Aplicar los estilos de scrollbar personalizados
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = customScrollbarStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Aplicar el tema claro/oscuro
  useEffect(() => {
    document.documentElement.classList.toggle('dark-theme', darkMode);
    document.documentElement.classList.toggle('light-theme', !darkMode);
  }, [darkMode]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    window.location.href = "/admin/login";
  };

  const handleCardClick = (tabValue: string) => {
    setActiveTab(tabValue);
    setView("detail");
  };

  const handleSearchResultClick = (item: { id: string | number; name: string; type: string }) => {
    // Navegar a la sección correspondiente y seleccionar el elemento
    switch(item.type) {
      case "product":
        setActiveTab("products");
        // Aquí podría implementarse lógica para seleccionar el producto específico
        break;
      case "category":
        setActiveTab("categories");
        break;
      case "used":
        setActiveTab("used-machinery");
        break;
      case "used-category":
        setActiveTab("used-categories");
        break;
      case "spare-part":
        setActiveTab("spare-parts");
        break;
    }

    setView("detail");
    setShowSearchResults(false);
    setSearchQuery("");
  };

  const clearSearch = () => {
    setSearchQuery("");
    setShowSearchResults(false);
  };

  // Recuperar todas las tarjetas de acciones
  const allActionCards = () => (
    <>
      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 0.5,
          duration: 0.4
        }}
        onClick={() => handleCardClick("products")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-blue-500/10' : 'hover:shadow-blue-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-blue-400 to-blue-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-blue-900/80' : 'bg-blue-200'} transition-colors duration-300`}>
                <Package className="h-6 w-6 text-blue-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Productos Nuevos</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Gestionar el catálogo de productos, añadir nuevos, editar existentes o eliminar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{dashboardStats.productsTotal} productos</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Accede a todas las operaciones relacionadas con los productos del catálogo principal.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-blue-500 font-medium">Gestionar productos</span>
            <div className={`${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-blue-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 0.6,
          duration: 0.4
        }}
        onClick={() => handleCardClick("categories")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-purple-500/10' : 'hover:shadow-purple-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-purple-400 to-purple-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-purple-900/80' : 'bg-purple-200'} transition-colors duration-300`}>
                <Folder className="h-6 w-6 text-purple-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Categorías</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Organizar la estructura de categorías y subcategorías de productos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-purple-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-purple-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-purple-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{dashboardStats.categoriesTotal} categorías</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Gestiona el árbol de categorías, crea nuevas o modifica las existentes.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-purple-500 font-medium">Gestionar categorías</span>
            <div className={`${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-purple-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 0.7,
          duration: 0.4
        }}
        onClick={() => handleCardClick("used-machinery")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-amber-500/10' : 'hover:shadow-amber-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-amber-900/80' : 'bg-amber-200'} transition-colors duration-300`}>
                <Archive className="h-6 w-6 text-amber-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Maquinaria de Ocasión</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Administrar el inventario de maquinaria usada y de ocasión
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{dashboardStats.usedMachineryTotal} maquinarias</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Añade, edita o elimina equipos de la sección de maquinaria de ocasión.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-amber-500 font-medium">Gestionar maquinaria</span>
            <div className={`${darkMode ? 'bg-amber-900/30' : 'bg-amber-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-amber-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 0.8,
          duration: 0.4
        }}
        onClick={() => handleCardClick("used-categories")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-orange-500/10' : 'hover:shadow-orange-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-orange-400 to-orange-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-orange-900/50' : 'bg-orange-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-orange-900/80' : 'bg-orange-200'} transition-colors duration-300`}>
                <Tag className="h-6 w-6 text-orange-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Categorías de Ocasión</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Organizar las categorías para productos de ocasión
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-orange-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-orange-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-orange-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>8 categorías</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Crea y gestiona el árbol de categorías para la sección de maquinaria usada.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-orange-500 font-medium">Gestionar categorías</span>
            <div className={`${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-orange-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 0.9,
          duration: 0.4
        }}
        onClick={() => handleCardClick("calls")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-green-500/10' : 'hover:shadow-green-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-green-400 to-green-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-green-900/50' : 'bg-green-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-green-900/80' : 'bg-green-200'} transition-colors duration-300`}>
                <Phone className="h-6 w-6 text-green-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Llamadas Programadas</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Gestionar las solicitudes de llamadas de clientes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-green-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-green-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{dashboardStats.callsTotal} solicitudes</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Revisa y gestiona todas las solicitudes de contacto pendientes.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-green-500 font-medium">Ver solicitudes</span>
            <div className={`${darkMode ? 'bg-green-900/30' : 'bg-green-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-green-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 1.0,
          duration: 0.4
        }}
        onClick={() => handleCardClick("users")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-blue-400/10' : 'hover:shadow-blue-400/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-blue-300 to-blue-500 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-blue-900/80' : 'bg-blue-200'} transition-colors duration-300`}>
                <Users className="h-6 w-6 text-blue-400" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Usuarios</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Gestionar usuarios y asignar máquinas compradas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-400"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-blue-400/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-blue-400/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>4 usuarios</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Administra usuarios registrados y asigna máquinas compradas.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-blue-400 font-medium">Gestionar usuarios</span>
            <div className={`${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-blue-400" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 1.1,
          duration: 0.4
        }}
        onClick={() => handleCardClick("spare-parts")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-teal-500/10' : 'hover:shadow-teal-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-teal-400 to-teal-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-teal-900/50' : 'bg-teal-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-teal-900/80' : 'bg-teal-200'} transition-colors duration-300`}>
                <Settings className="h-6 w-6 text-teal-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Recambios</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Gestionar recambios y asignarlos a los productos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-teal-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-teal-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-teal-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>12 recambios</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Administra el catálogo de repuestos y asígnaselos a las máquinas correspondientes.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-teal-500 font-medium">Gestionar recambios</span>
            <div className={`${darkMode ? 'bg-teal-900/30' : 'bg-teal-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-teal-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 1.2,
          duration: 0.4
        }}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-gray-500/10' : 'hover:shadow-gray-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-gray-400 to-gray-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-200'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-gray-700' : 'bg-gray-300'} transition-colors duration-300`}>
                <Settings className="h-6 w-6 text-gray-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Configuración</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Ajustar parámetros generales del sitio web
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-gray-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-gray-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-gray-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Último: hace 2 días</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Configura opciones globales, banners y promociones destacadas.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-gray-500 font-medium">Acceder a configuración</span>
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-200'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-gray-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
          delay: 1.1,
          duration: 0.4
        }}
        onClick={() => handleCardClick("catalogs")}
      >
        <Card className={`${darkMode ? 'bg-gradient-to-br from-[#111213] to-[#16171B]' : 'bg-gradient-to-br from-white to-[#f8f8f8]'} border-0 hover:shadow-xl ${darkMode ? 'hover:shadow-indigo-500/10' : 'hover:shadow-indigo-500/20'} cursor-pointer overflow-hidden group`}>
          <div className="h-1.5 bg-gradient-to-r from-indigo-400 to-indigo-600 w-full"></div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className={`${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'} p-2.5 rounded-lg group-hover:${darkMode ? 'bg-indigo-900/80' : 'bg-indigo-200'} transition-colors duration-300`}>
                <FileText className="h-6 w-6 text-indigo-500" />
              </div>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>Catálogos PDF</CardTitle>
            </div>
            <CardDescription className={darkMode ? 'text-white/60' : 'text-gray-600'}>
              Gestionar los catálogos de productos en formato PDF
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                <span className="inline-block h-2 w-2 rounded-full bg-indigo-500"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-indigo-500/60"></span>
                <span className="inline-block h-2 w-2 rounded-full bg-indigo-500/30"></span>
              </div>
              <span className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>2 catálogos</span>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Añade y gestiona los catálogos en PDF que se muestran en la sección de catálogos.</p>
          </CardContent>
          <CardFooter className={`flex justify-between items-center border-t ${darkMode ? 'border-white/10' : 'border-gray-200'} pt-4`}>
            <span className="text-sm text-indigo-500 font-medium">Gestionar catálogos</span>
            <div className={`${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'} p-1.5 rounded-full transition-transform group-hover:translate-x-1`}>
              <ChevronRight className="h-4 w-4 text-indigo-500" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>
    </>
  );

  // Función para manejar la creación de nuevos elementos
  const handleCreateNew = (type: string) => {
    // Mapeo del tipo a la pestaña correspondiente
    const tabMapping: Record<string, string> = {
      "products": "products",
      "categories": "categories",
      "used-machinery": "used-machinery",
      "used-categories": "used-categories",
      "spare-parts": "spare-parts",
      "calls": "calls",
      "catalogs": "catalogs"
    };

    // Cambiar a la vista correcta
    setView("detail");

    // Establecer la pestaña activa según el tipo
    if (tabMapping[type]) {
      setActiveTab(tabMapping[type]);
    }

    // Mostrar toast de confirmación
    toast({
      title: "Creando nuevo elemento",
      description: `Preparando formulario para crear: ${type}`,
    });

    // Cerrar el menú flotante
    setFabOpen(false);
  };

  return (
    <div className={`min-h-screen custom-scrollbar ${darkMode ? 'bg-gradient-to-b from-[#060609] to-[#0F0F12]' : 'bg-gradient-to-b from-[#f5f5f7] to-[#e5e5e7]'}`}>
      {/* Pantalla de bienvenida */}
      <AnimatePresence>
        {showWelcomeScreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.3,
                duration: 0.5,
                type: "spring",
                stiffness: 100
              }}
              className="text-center"
            >
              <img
                src={logoBlanco}
                alt="Innotec Logo"
                className="h-24 mx-auto mb-6"
              />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 0.8, duration: 1 }}
                className="h-1 bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] mx-auto mt-4 rounded-full"
              />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}
              >
                Panel de Administración
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className={darkMode ? 'text-white/60' : 'text-gray-600'}
              >
                Iniciando sesión...
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Partículas de fondo */}
      {darkMode && (
        <Particles
          id="tsparticles"
          init={particlesInit}
          className="absolute inset-0 z-0"
          options={{
            fullScreen: false,
            background: {
              color: {
                value: "transparent",
              },
            },
            fpsLimit: 60,
            particles: {
              color: {
                value: "#ffffff",
              },
              links: {
                color: "#ffffff",
                distance: 150,
                enable: true,
                opacity: 0.1,
                width: 1,
              },
              collisions: {
                enable: false,
              },
              move: {
                direction: "none",
                enable: true,
                outMode: "bounce",
                random: false,
                speed: 0.3,
                straight: false,
              },
              number: {
                density: {
                  enable: true,
                  area: 800,
                },
                value: 30,
              },
              opacity: {
                value: 0.1,
              },
              shape: {
                type: "circle",
              },
              size: {
                random: true,
                value: 2,
              },
            },
            detectRetina: true,
          }}
        />
      )}

      {/* Botón flotante de acción rápida (FAB) */}
      <div className="fixed right-6 bottom-6 z-50">
        <AnimatePresence>
          {fabOpen && (
            <>
              {/* Overlay para cerrar el menú al hacer clic fuera */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setFabOpen(false)}
              />

              {/* Menú de opciones */}
              <motion.div
                className="absolute bottom-16 right-0 z-50 flex flex-col-reverse items-end gap-2"
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <motion.button
                  onClick={() => handleCreateNew("products")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Package className="h-4 w-4" />
                  <span>Nuevo Producto</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("categories")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Folder className="h-4 w-4" />
                  <span>Nueva Categoría</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("used-machinery")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Archive className="h-4 w-4" />
                  <span>Nueva Maquinaria Usada</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("used-categories")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Tag className="h-4 w-4" />
                  <span>Nueva Categoría de Ocasión</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("spare-parts")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Settings className="h-4 w-4" />
                  <span>Nuevo Recambio</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("calls")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Phone className="h-4 w-4" />
                  <span>Nueva Llamada</span>
                </motion.button>

                <motion.button
                  onClick={() => handleCreateNew("catalogs")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500 text-white shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <FileText className="h-4 w-4" />
                  <span>Nuevo Catálogo PDF</span>
                </motion.button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Botón principal */}
        <motion.button
          className={`${fabOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90'} text-white p-4 rounded-full shadow-lg flex items-center justify-center shadow-amber-900/20`}
          onClick={() => setFabOpen(!fabOpen)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          animate={{ rotate: fabOpen ? 135 : 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          <Plus className="h-6 w-6" />
        </motion.button>
      </div>

      {/* Resto del contenido (oculto durante la pantalla de bienvenida) */}
      <div className={`relative z-10 transition-opacity duration-300 ${showWelcomeScreen ? 'opacity-0' : 'opacity-100'}`}>
        {/* Navbar mejorado con efecto de vidrio */}
        <div className={`fixed top-0 left-0 right-0 z-50 ${darkMode ? 'bg-black/40' : 'bg-white/40'} backdrop-blur-lg border-b ${darkMode ? 'border-white/10' : 'border-black/10'} shadow-2xl`}>
          <div className="container mx-auto px-4 flex items-center justify-between h-16">
            {/* Logo y título con animación sutil */}
            <div className="flex items-center space-x-4">
              <motion.div
                className="relative flex items-center"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <img src={logoBlanco} alt="Innotec Logo" className="h-9" />
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-[#FCAA0D] rounded-full animate-pulse"></div>
              </motion.div>
              <div className="h-8 w-px bg-white/10 hidden md:block"></div>
              <div className="hidden md:flex items-center">
                <h1 className="text-lg font-semibold text-white">
                  Panel de <span className="text-[#FCAA0D]">Administración</span>
                </h1>
              </div>
            </div>

            {/* Barra de búsqueda mejorada - Solo visible en desktop */}
            <div className="hidden md:flex items-center max-w-md w-full mx-4 relative">
              <div className="relative w-full">
                <div className="absolute left-3 top-2.5 h-5 w-5 text-white/30 flex items-center justify-center">
                  <Search className="h-4 w-4" />
                </div>
                <motion.input
                  initial={{ width: "100%" }}
                  whileFocus={{ width: "105%", boxShadow: "0 0 0 2px rgba(252, 170, 13, 0.3)" }}
                  transition={{ duration: 0.2 }}
                  type="text"
                  placeholder="Buscar productos, categorías, maquinaria..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-full bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-transparent text-sm text-white transition-all duration-200 hover:bg-white/8"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-2.5 text-white/30 hover:text-white transition-colors duration-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Resultados de búsqueda */}
              <AnimatePresence>
              {showSearchResults && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-[#16171B]/90 border border-white/10 rounded-xl shadow-xl max-h-[70vh] overflow-y-auto z-50 backdrop-blur-xl"
                  >
                  {searchResults.products.length === 0 &&
                   searchResults.categories.length === 0 &&
                   searchResults.usedMachinery.length === 0 &&
                   searchResults.usedCategories.length === 0 &&
                   searchResults.spareParts.length === 0 ? (
                      <div className="py-6 px-4 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Search className="h-10 w-10 text-white/10 mb-2" />
                          <p className="text-white/60">No se encontraron resultados para "{searchQuery}"</p>
                          <p className="text-xs text-white/30 mt-1">Intenta con otra búsqueda</p>
                        </div>
                    </div>
                  ) : (
                    <>
                      {searchResults.products.length > 0 && (
                        <div className="py-2">
                            <h3 className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Productos</h3>
                          {searchResults.products.map(product => (
                            <button
                              key={`product-${product.id}`}
                                className="w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center text-white transition-colors duration-150"
                              onClick={() => handleSearchResultClick(product)}
                            >
                                <div className="bg-blue-900/20 p-1.5 rounded-lg mr-3">
                                  <Package className="h-4 w-4 text-blue-400" />
                                </div>
                                <span className="font-medium">{product.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.categories.length > 0 && (
                          <div className="py-2 border-t border-white/5">
                            <h3 className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Categorías</h3>
                          {searchResults.categories.map(category => (
                            <button
                              key={`category-${category.id}`}
                                className="w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center text-white transition-colors duration-150"
                              onClick={() => handleSearchResultClick(category)}
                            >
                                <div className="bg-purple-900/20 p-1.5 rounded-lg mr-3">
                                  <Folder className="h-4 w-4 text-purple-400" />
                                </div>
                                <span className="font-medium">{category.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.usedMachinery.length > 0 && (
                          <div className="py-2 border-t border-white/5">
                            <h3 className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Maquinaria de Ocasión</h3>
                          {searchResults.usedMachinery.map(item => (
                            <button
                              key={`used-${item.id}`}
                                className="w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center text-white transition-colors duration-150"
                              onClick={() => handleSearchResultClick(item)}
                            >
                                <div className="bg-amber-900/20 p-1.5 rounded-lg mr-3">
                                  <Archive className="h-4 w-4 text-amber-400" />
                                </div>
                                <span className="font-medium">{item.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.usedCategories.length > 0 && (
                          <div className="py-2 border-t border-white/5">
                            <h3 className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Categorías de Ocasión</h3>
                          {searchResults.usedCategories.map(item => (
                            <button
                              key={`used-category-${item.id}`}
                                className="w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center text-white transition-colors duration-150"
                              onClick={() => handleSearchResultClick(item)}
                            >
                                <div className="bg-orange-900/20 p-1.5 rounded-lg mr-3">
                                  <Tag className="h-4 w-4 text-orange-400" />
                                </div>
                                <span className="font-medium">{item.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.spareParts.length > 0 && (
                        <div className="py-2 border-t border-white/5">
                          <h3 className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Recambios</h3>
                          {searchResults.spareParts.map(item => (
                            <button
                              key={`spare-part-${item.id}`}
                              className="w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center text-white transition-colors duration-150"
                              onClick={() => handleSearchResultClick(item)}
                            >
                              <div className="bg-teal-900/20 p-1.5 rounded-lg mr-3">
                                <Settings className="h-4 w-4 text-teal-400" />
                              </div>
                              <span className="font-medium">{item.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  </motion.div>
              )}
              </AnimatePresence>
            </div>

            {/* Acciones y perfil mejorado */}
            <div className="flex items-center space-x-3">
              {/* Botón de crear nuevo */}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden md:flex items-center gap-2 bg-gradient-to-r from-[#FCAA0D]/20 to-[#FF8A00]/20 text-[#FCAA0D] hover:bg-[#FCAA0D]/20 rounded-full px-4 border border-[#FCAA0D]/20"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>Nuevo</span>
                </Button>
              </motion.div>

              {/* Notificaciones */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="relative p-2 rounded-full hover:bg-white/5 transition-colors duration-200"
              >
                <Bell className="h-5 w-5 text-white/70" />
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">3</span>
              </motion.button>

              {/* Perfil de usuario mejorado - Versión desktop */}
              <div className="user-menu relative">
                <motion.button
                  whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.08)" }}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="hidden md:flex items-center space-x-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-white/5 transition-colors duration-200 user-menu-button"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#FCAA0D] to-[#FF8A00] text-white flex items-center justify-center shadow-lg">
                  {username.charAt(0).toUpperCase()}
                </div>
                  <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-white">{username}</span>
                  <span className="text-xs text-white/50">Administrador</span>
                </div>
                  <ChevronDown className="h-4 w-4 text-white/50 ml-1" />
                </motion.button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15, type: "spring", stiffness: 500, damping: 30 }}
                      className="absolute right-0 mt-2 w-48 rounded-xl bg-[#16171B]/95 shadow-2xl border border-white/10 overflow-hidden z-50 backdrop-blur-xl"
                    >
                      <div className="py-2 px-3 border-b border-white/5">
                        <p className="text-sm font-medium text-white">{username}</p>
                        <p className="text-xs text-white/50">Administrador</p>
                      </div>
                      <div className="py-1">
                        <button className="w-full px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>Mi perfil</span>
                        </button>
                        <button className="w-full px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center space-x-2">
                          <Settings className="h-4 w-4" />
                          <span>Configuración</span>
                        </button>
                      </div>
                      <div className="border-t border-white/5 py-1">
                        <button
                          onClick={handleLogout}
                          className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-white/5 flex items-center space-x-2"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Cerrar sesión</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Botón de menú móvil mejorado */}
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-white/70 hover:bg-white/5 rounded-full"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </motion.div>

              {/* Añadir botón de cambio de tema */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'} transition-colors duration-200`}
              >
                {darkMode ? (
                  <Sun className={`h-5 w-5 ${darkMode ? 'text-white/70' : 'text-black/70'}`} />
                ) : (
                  <Moon className={`h-5 w-5 ${darkMode ? 'text-white/70' : 'text-black/70'}`} />
                )}
              </button>
            </div>
          </div>

          {/* Menú móvil mejorado */}
          <AnimatePresence>
          {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden bg-[#121316] border-t border-white/5 py-3 px-4 overflow-hidden shadow-2xl"
              >
                <div className="flex items-center bg-white/5 rounded-full p-2 mb-4 relative">
                  <Search className="h-4 w-4 text-white/40 mx-2" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent border-none focus:outline-none text-sm w-full text-white"
                />
                {searchQuery && (
                    <button onClick={clearSearch} className="mr-2 text-white/50">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Resultados de búsqueda móvil */}
              {showSearchResults && searchQuery.trim().length >= 2 && (
                  <div className="mt-1 mb-4 bg-[#1A1A1C] border border-white/10 rounded-lg max-h-[50vh] overflow-y-auto">
                  {searchResults.products.length === 0 &&
                   searchResults.categories.length === 0 &&
                   searchResults.usedMachinery.length === 0 &&
                   searchResults.usedCategories.length === 0 &&
                   searchResults.spareParts.length === 0 ? (
                    <div className="py-4 px-3 text-center text-white/60">
                      No se encontraron resultados
                    </div>
                  ) : (
                    <>
                      {searchResults.products.length > 0 && (
                        <>
                            <h3 className="px-3 py-2 text-xs font-semibold text-white/40 uppercase tracking-wide">Productos</h3>
                          {searchResults.products.map(product => (
                            <button
                              key={`mobile-product-${product.id}`}
                              className="w-full px-3 py-2 text-left hover:bg-white/5 flex items-center text-white text-sm"
                              onClick={() => {
                                handleSearchResultClick(product);
                                setMobileMenuOpen(false);
                              }}
                            >
                                <div className="bg-blue-900/20 p-1.5 rounded-lg mr-2">
                                  <Package className="h-3 w-3 text-blue-400" />
                                </div>
                              <span>{product.name}</span>
                            </button>
                          ))}
                        </>
                      )}

                          {/* Más secciones para otras categorías de búsqueda... */}
                          </>
                        )}
                  </div>
                )}

                  {/* Acciones principales */}
                  <div className="space-y-1 mb-4">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm py-2.5 px-3 text-white hover:bg-white/5 rounded-lg"
                                onClick={() => {
                      setView("launcher");
                                setMobileMenuOpen(false);
                              }}
                            >
                      <LayoutDashboard className="h-4 w-4 mr-3 text-[#FCAA0D]" />
                      Panel principal
                    </Button>

                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm py-2.5 px-3 text-white hover:bg-white/5 rounded-lg"
                    >
                      <Calendar className="h-4 w-4 mr-3 text-green-400" />
                      Calendario
                    </Button>

                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm py-2.5 px-3 text-white hover:bg-white/5 rounded-lg"
                    >
                      <Settings className="h-4 w-4 mr-3 text-gray-400" />
                      Configuración
                    </Button>
                  </div>

                  {/* Información del usuario */}
                  <div className="bg-white/5 rounded-lg p-3 mb-3 flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] text-white flex items-center justify-center">
                    {username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{username}</p>
                    <p className="text-xs text-white/50">Administrador</p>
                  </div>
                </div>

                  <Button
                    variant="ghost"
                    className="w-full justify-start rounded-lg text-sm py-2.5 px-3 text-red-400 hover:bg-white/5"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Cerrar sesión
                  </Button>
                </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Contenido principal con animaciones mejoradas */}
        <div className="relative z-10">
          <AnimatePresence mode="wait">
          {view === "launcher" ? (
              <motion.div
                key="launcher"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 100, damping: 20 }}
                className="container mx-auto py-10 px-4 max-w-7xl mt-16"
              >
                <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <motion.h1
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1, duration: 0.4 }}
                      className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}
                    >
                      Bienvenido, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00]">{username}</span>
                    </motion.h1>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className={darkMode ? 'text-white/60' : 'text-gray-600'}
                    >
                      Panel de control de Innotec. ¿Qué te gustaría gestionar hoy?
                    </motion.p>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    className="mt-4 sm:mt-0 flex space-x-2"
                  >
                    <Button className="bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90 text-black font-medium rounded-full shadow-lg shadow-amber-900/20">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Crear nuevo
                    </Button>
                  </motion.div>
                </div>

                {/* Stats summary con mejoras visuales */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className={`${darkMode ? 'bg-gradient-to-br from-blue-500/20 to-blue-700/10 border-blue-500/20' : 'bg-gradient-to-br from-blue-500/10 to-blue-700/5 border-blue-500/15'} border rounded-xl p-4 backdrop-blur-sm shadow-lg ${darkMode ? 'shadow-blue-900/5' : 'shadow-blue-400/20'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Productos</p>
                        <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.productsTotal}</h3>
                        <p className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'} mt-1`}>{dashboardStats.productsNew} nuevos este mes</p>
                      </div>
                      <div className={`${darkMode ? 'bg-blue-500/20' : 'bg-blue-500/10'} p-2 rounded-lg`}>
                        <Package className="h-5 w-5 text-blue-400" />
                      </div>
                    </div>
                    {/* Barra de progreso animada */}
                    <div className="mt-3 w-full h-1 bg-blue-500/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '70%' }}
                        transition={{ delay: 0.3, duration: 1 }}
                        className="h-full bg-blue-500"
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className={`${darkMode ? 'bg-gradient-to-br from-purple-500/20 to-purple-700/10 border-purple-500/20' : 'bg-gradient-to-br from-purple-500/10 to-purple-700/5 border-purple-500/15'} border rounded-xl p-4 backdrop-blur-sm shadow-lg ${darkMode ? 'shadow-purple-900/5' : 'shadow-purple-400/20'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">Categorías</p>
                        <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dashboardStats.categoriesTotal}</h3>
                        <p className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'} mt-1`}>{dashboardStats.categoriesNew} nuevas este mes</p>
                      </div>
                      <div className={`${darkMode ? 'bg-purple-500/20' : 'bg-purple-500/10'} p-2 rounded-lg`}>
                        <Folder className="h-5 w-5 text-purple-400" />
                      </div>
                    </div>
                    {/* Barra de progreso animada */}
                    <div className="mt-3 w-full h-1 bg-purple-500/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '45%' }}
                        transition={{ delay: 0.4, duration: 1 }}
                        className="h-full bg-purple-500"
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                    className="bg-gradient-to-br from-amber-500/20 to-amber-700/10 border border-amber-500/20 rounded-xl p-4 backdrop-blur-sm shadow-lg shadow-amber-900/5"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Maq. de Ocasión</p>
                        <h3 className="text-2xl font-bold text-white">18</h3>
                        <p className="text-xs text-white/50 mt-1">5 nuevas este mes</p>
                      </div>
                      <div className="bg-amber-500/20 p-2 rounded-lg">
                        <Archive className="h-5 w-5 text-amber-400" />
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="bg-gradient-to-br from-green-500/20 to-green-700/10 border border-green-500/20 rounded-xl p-4 backdrop-blur-sm shadow-lg shadow-green-900/5"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Llamadas</p>
                        <h3 className="text-2xl font-bold text-white">8</h3>
                        <p className="text-xs text-white/50 mt-1">3 pendientes</p>
                      </div>
                      <div className="bg-green-500/20 p-2 rounded-lg">
                        <Phone className="h-5 w-5 text-green-400" />
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Panel principal con dos columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  {/* Columna izquierda: tarjetas de gestión */}
                  <div className="lg:col-span-2">
                    <motion.h2
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                      className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}
                    >
                      Gestión rápida
                    </motion.h2>

                    {/* Tarjetas de gestión mejoradas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {allActionCards()}
                    </div>
                  </div>

                  {/* Columna derecha: actividad reciente y estadísticas */}
                  <div className="space-y-6">
                    {/* Actividad reciente */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                      className={`${darkMode ? 'bg-[#16171B]/80' : 'bg-white/80'} backdrop-blur-sm rounded-xl border ${darkMode ? 'border-white/5' : 'border-black/5'} shadow-xl p-5`}
                    >
                      <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Actividad reciente</h3>
                      <div className={`space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar`}>
                        {dashboardStats.recentActivity.map((activity) => (
                          <div
                            key={activity.id}
                            className={`p-3 rounded-lg ${darkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'} transition-colors flex items-start gap-4`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activity.action.includes("Producto") ? "bg-blue-500/20" :
                              activity.action.includes("Categoría") ? "bg-purple-500/20" :
                              activity.action.includes("Llamada") ? "bg-green-500/20" : "bg-amber-500/20"
                            }`}>
                              {activity.action.includes("Producto") ? <Package className="h-5 w-5 text-blue-400" /> :
                              activity.action.includes("Categoría") ? <Folder className="h-5 w-5 text-purple-400" /> :
                              activity.action.includes("Llamada") ? <Phone className="h-5 w-5 text-green-400" /> :
                              <Archive className="h-5 w-5 text-amber-400" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{activity.action}</p>
                                <span className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>{activity.time}</span>
                              </div>
                              <p className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>{activity.item}</p>
                              <p className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'} mt-1`}>Por: {activity.user}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Productos más vistos */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.4 }}
                      className={`${darkMode ? 'bg-[#16171B]/80' : 'bg-white/80'} backdrop-blur-sm rounded-xl border ${darkMode ? 'border-white/5' : 'border-black/5'} shadow-xl p-5`}
                    >
                      <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Productos más vistos</h3>
                      <div className="space-y-4">
                        {dashboardStats.topProducts.map((product, index) => (
                          <div key={index} className="space-y-2">
                            <div className="flex justify-between">
                              <span className={`text-sm font-medium ${darkMode ? 'text-white/90' : 'text-gray-800'}`}>{product.name}</span>
                              <span className={`text-xs font-medium ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>{product.views} vistas</span>
                            </div>
                            <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-white/10' : 'bg-black/10'}`}>
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${product.percentage}%` }}
                                transition={{ delay: 0.5 + index * 0.1, duration: 1 }}
                                className={`h-full rounded-full ${
                                  index === 0 ? 'bg-blue-500' :
                                  index === 1 ? 'bg-purple-500' :
                                  'bg-amber-500'
                                }`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        className={`w-full mt-4 ${darkMode ? 'border-white/10 text-white hover:bg-white/5' : 'border-black/10 text-gray-900 hover:bg-black/5'}`}
                      >
                        Ver estadísticas completas
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="detail"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 100, damping: 20 }}
                className="container mx-auto py-8 px-4 mt-16"
              >
                <Card className={`shadow-2xl ${darkMode ? 'bg-gradient-to-b from-[#111213] to-[#16171B]' : 'bg-gradient-to-b from-white to-[#f8f8f8]'} border-0 ${darkMode ? 'border-white/5' : 'border-black/5'} overflow-hidden`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setView("launcher")}
                          className="rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
                        >
                          <Home className="h-5 w-5" />
                        </Button>
                      </motion.div>
                      <div>
                        <CardTitle className="text-2xl font-bold mb-1 text-white">Panel de Administración</CardTitle>
                        <CardDescription className="text-white/60">
                          Gestiona productos, categorías y maquinaria de ocasión
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="text-white">
                    <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                      <TabsList className="mb-6 bg-[#1A1A1C] rounded-lg p-1 min-w-max">
                        <TabsTrigger
                          value="products"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Productos
                        </TabsTrigger>

                        <TabsTrigger
                          value="categories"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Folder className="w-4 h-4 mr-2" />
                          Categorías
                        </TabsTrigger>

                        <TabsTrigger
                          value="used-machinery"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Maquinaria Usada
                        </TabsTrigger>

                        <TabsTrigger
                          value="used-categories"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Tag className="w-4 h-4 mr-2" />
                          Categorías Usadas
                        </TabsTrigger>

                        <TabsTrigger
                          value="spare-parts"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Recambios
                        </TabsTrigger>

                        <TabsTrigger
                          value="calls"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Llamadas
                        </TabsTrigger>

                        <TabsTrigger
                          value="users"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Usuarios
                        </TabsTrigger>

                        <TabsTrigger
                          value="catalogs"
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FCAA0D] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-md transition-all duration-200"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Catálogos
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <TabsContent value="products">
                          <ProductsTab />
                        </TabsContent>

                        <TabsContent value="categories">
                          <CategoriesTab />
                        </TabsContent>

                        <TabsContent value="used-machinery">
                          <UsedMachineryTab />
                        </TabsContent>

                        <TabsContent value="used-categories">
                          <UsedCategoriesTab />
                        </TabsContent>

                        <TabsContent value="spare-parts">
                          <SparePartsTab />
                        </TabsContent>

                        <TabsContent value="calls">
                          <ScheduleCallsTab />
                        </TabsContent>

                        <TabsContent value="users">
                          <UsersTab />
                        </TabsContent>

                        <TabsContent value="catalogs">
                          <CatalogsTab />
                        </TabsContent>
                      </motion.div>
                    </AnimatePresence>
                  </Tabs>
                </CardContent>
              </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
